import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Send } from "lucide-react";
import { Avatar, AvatarFallback, Badge, Button, Label, Skeleton, Textarea, cn, markScrollbarKind, userInitials } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { portal, type PortalTicketDetail } from "../../../lib/portal";
import { fmtDateTime, genericStatusVariant } from "../../../lib/format";

/**
 * One conversation: who said what, in order, and a box to answer in.
 *
 * Messages are blocks in one column rather than chat bubbles pushed to two
 * sides. A support answer is often several paragraphs with a list in it, and a
 * bubble capped at 80% of the width wraps that into a narrow strip. The reader's
 * own messages are tinted and say "you", so the two voices are still told apart
 * at a glance - by a word as well as by a colour.
 *
 * The messages scroll inside their own box so the answer field stays in reach
 * however long the conversation is; the box follows the newest message.
 */
export function TicketThread({
  id,
  onBack,
  onChanged,
}: {
  id: string;
  onBack: () => void;
  /** The conversation changed (a reply went out), so the list beside it is stale. */
  onChanged: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [ticket, setTicket] = useState<PortalTicketDetail | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    // An id this reader may not open answers 404; the list is the answer.
    void portal.ticket(id).then((d) => { if (live) setTicket(d); }).catch(() => { if (live) onBack(); });
    return () => {
      live = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The message box is a small scroll box inside the app. On a machine that
  // draws classic scrollbars the kit restyles it, and needs to be told so.
  useEffect(() => {
    markScrollbarKind();
  }, []);

  // Follow the newest message. The box is scrolled itself, not the page:
  // `scrollIntoView` would also drag the whole window down on every reply.
  const count = ticket?.messages.length ?? 0;
  useEffect(() => {
    const box = boxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [count]);

  async function send() {
    const body = reply.trim();
    if (!body || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const fresh = await portal.replyTicket(id, body);
      setTicket(fresh);
      setReply("");
      onChanged();
    } catch {
      // The text stays in the box. A reply that failed to send and was also
      // wiped would cost the client the paragraph they just wrote.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-border px-5 py-4 md:px-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label={t("portal.support.back")}
          className="-ml-2 shrink-0 lg:hidden"
        >
          <ArrowLeft />
        </Button>
        <div className="min-w-0 flex-1">
          {ticket ? (
            <>
              <h2 className="text-lg font-bold tracking-tight text-balance">{ticket.subject}</h2>
              <p className="mt-0.5 text-sm text-foreground/70">
                {ticket.number} · {ticket.client.name}
              </p>
            </>
          ) : (
            <>
              <Skeleton className="h-6 w-56 max-w-full" />
              <Skeleton className="mt-2 h-4 w-40" />
            </>
          )}
        </div>
        {ticket && (
          <Badge variant={genericStatusVariant(ticket.status)} className="mt-1">
            {t(`portal.ticketStatus.${ticket.status}`)}
          </Badge>
        )}
      </div>

      <div
        ref={boxRef}
        role="log"
        aria-busy={!ticket}
        aria-label={t("portal.support.messages")}
        className="scroll-soft max-h-[min(60vh,36rem)] flex-1 space-y-3 overflow-y-auto px-5 py-5 md:px-6"
      >
        {!ticket ? (
          <>
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-20 w-full rounded-2xl" />
          </>
        ) : (
          ticket.messages.map((m) => {
            const mine = m.authorType === "client";
            return (
              <article
                key={m.id}
                className={cn("rounded-2xl px-4 py-3.5", mine ? "bg-primary/10" : "border border-border")}
              >
                <div className="flex items-center gap-2.5">
                  <Avatar className="size-7 shrink-0">
                    <AvatarFallback className="text-[10px]">{userInitials(m.author)}</AvatarFallback>
                  </Avatar>
                  <p className="min-w-0 text-sm">
                    <span className="font-semibold">{m.author}</span>
                    <span className="text-foreground/70">
                      {mine && <> · {t("portal.support.you")}</>} · {fmtDateTime(m.createdAt, locale)}
                    </span>
                  </p>
                </div>
                <p className="mt-2.5 max-w-prose whitespace-pre-wrap break-words text-[15px] leading-relaxed">{m.body}</p>
              </article>
            );
          })
        )}
      </div>

      <div className="border-t border-border bg-muted/40 px-5 py-4 md:px-6">
        <Label htmlFor="ticket-reply">{t("portal.support.reply")}</Label>
        <Textarea
          id="ticket-reply"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={t("portal.support.replyPlaceholder")}
          rows={3}
          maxLength={10000}
          disabled={!ticket}
          className="mt-1.5 resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void send();
          }}
        />
        {failed && <p role="alert" className="mt-2 text-sm text-destructive">{t("portal.support.sendFailed")}</p>}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="text-xs text-foreground/70 max-sm:hidden">{t("portal.support.sendHint")}</p>
          <Button onClick={() => void send()} loading={busy} disabled={!reply.trim()} className="max-sm:h-11 max-sm:w-full">
            <Send />
            {t("portal.support.send")}
          </Button>
        </div>
      </div>
    </>
  );
}
